import { CircleHelp, ExternalLink } from 'lucide-react';
import { ChangeEvent, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { comparisonMetricOptions } from '.';
import { useGetMetricTargetsQuery } from '../../api/metric-targets';
import { DiscardChangesDialog } from '../../components/app/DiscardChangesDialog';
import { Button } from '../../components/Button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/Dialog';
import { NumberInput } from '../../components/Input/NumberInput';
import { Slider } from '../../components/Input/Slider';
import { InputLabel } from '../../components/Input/TextInput';
import { Skeleton } from '../../components/Skeleton';
import { useToaster } from '../../hooks/useToaster';
import { useEditMonthlyTargets } from '../hooks/useEditMonthlyTargets';

export function EditMonthlyTargetsDialog({ children }: PropsWithChildren) {
  const [open, setOpen] = useState(false);
  const [confirmDiscardChanges, setConfirmDiscardChanges] = useState(false);

  const { data: targets, isLoading } = useGetMetricTargetsQuery({ timespan: 'month' });
  const [formValues, setFormValues] = useState<Record<string, number | undefined>>({});
  const { editTargets, isEditing } = useEditMonthlyTargets({ targets: targets ?? [], formValues });
  const toast = useToaster();

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFormValues(Object.fromEntries((targets ?? []).map(({ metric, value }) => [metric, value])));
  }, [open, targets]);

  const onConfirm = useCallback(async () => {
    try {
      await editTargets();
      toast.success('Successfully updated monthly targets.');
      setOpen(false);
    } catch {
      toast.error('Failed to update monthly targets. Please try again.');
    }
  }, [editTargets, toast]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (isEditing) {
        return;
      }

      if (!open) {
        const isFormDirty = comparisonMetricOptions.some((option) => {
          const target = targets?.find((t) => t.metric === option.value);
          return target?.value !== formValues[option.value];
        });

        setConfirmDiscardChanges(isFormDirty);
        setOpen(isFormDirty);
      } else {
        setOpen(open);
      }
    },
    [isEditing, targets, formValues]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children}

      <DialogContent className="sm:max-w-[500px]" data-testid="edit-monthly-targets-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-row items-center">
            Edit Monthly Targets
            <div title="Targets are only shown when the selected date range is between 28 and 31 days.">
              <CircleHelp className="ml-2 h-4 w-4" />
            </div>
          </DialogTitle>
        </DialogHeader>

        {comparisonMetricOptions
          .filter((o) => o.targetable)
          .map((option) => {
            if (isLoading) {
              return <Skeleton className="mb-1 h-8 w-full" key={option.value} />;
            }

            if (option.value === 'activePatientCount') {
              return (
                <div className="mb-6 flex flex-col gap-1" key={option.value}>
                  <InputLabel htmlFor={`monthly-target-${option.value}`} className="flex justify-between text-sm">
                    <span>{option.editLabel ?? option.label}</span>
                    <Link
                      to="/admin/clinics"
                      className="text-accent text-md flex cursor-pointer items-center justify-center gap-1 hover:brightness-110"
                    >
                      <ExternalLink className="w-4" /> Edit Monthly Censuses
                    </Link>
                  </InputLabel>
                  <div className="flex gap-2 text-sm">
                    <Slider
                      min={0}
                      max={100}
                      step={1}
                      value={[formValues[option.value] ?? 0]}
                      onValueChange={(value: number[]) => {
                        setFormValues((prev) => ({ ...prev, [option.value]: value[0] || undefined }));
                      }}
                      valueFormatter={(value) => (value[0] > 0 ? `${value[0]}%` : `Not set`)}
                      name={`monthly-target-${option.value}`}
                    />
                  </div>
                </div>
              );
            }

            return (
              <div className="flex flex-row items-center gap-2" key={option.value}>
                <NumberInput
                  className="w-32"
                  value={formValues[option.value]}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const value = isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber;
                    setFormValues((prev) => ({ ...prev, [option.value]: value }));
                  }}
                  step={1}
                  id={`monthly-target-${option.value}`}
                />
                <InputLabel htmlFor={`monthly-target-${option.value}`}>{option.editLabel ?? option.label}</InputLabel>
              </div>
            );
          })}

        <DialogFooter>
          <Button
            variant="accent"
            onClick={onConfirm}
            disabled={isLoading || isEditing}
            isLoading={isEditing}
            data-testid="edit-monthly-targets-confirm-button"
          >
            Confirm
          </Button>

          <DialogClose asChild>
            <Button variant="transparent">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>

      <DiscardChangesDialog
        open={confirmDiscardChanges}
        onConfirm={() => {
          setConfirmDiscardChanges(false);
          setOpen(false);
        }}
        onCancel={() => setConfirmDiscardChanges(false)}
      />
    </Dialog>
  );
}
